import { Box, Fade, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';

import { styled } from '@mui/material/styles';

const messages: string[] = [
    'Hello', // English
    'Vanakkam', // Tamil
    'Namaste', // Hindi/Sanskrit
    'Bonjour', // French
    'Hola', // Spanish
    'Ciao', // Italian
    'Namaskar', // Bengali
    'Ahoj', // Czech/Slovak
    'Salam', // Arabic/Persian/Urdu
    'Merhaba', // Turkish
    'Privet', // Russian
    'Aloha', // Hawaiian
    'Szia', // Hungarian
    'Tere', // Estonian
    'Shalom', // Hebrew
    'Bula', // Fijian
    'Zdravstvuy', // Russian (formal)
    'Sawubona', // Zulu
    'Mingalaba', // Burmese
    'Konnichiwa', // Japanese
    'Anyoung', // Korean (casual)
    'Jambo', // Swahili
];

const TYPING_SPEED = 150;
const DELETING_SPEED = 50;
const PAUSE_DURATION = 1500;
const FADE_DURATION = 50;

const Cursor = styled('span')(({ theme }) => ({
    '@keyframes blink': { to: { visibility: 'hidden' } },
    animation: 'blink 1s steps(2, start) infinite',
    backgroundColor: theme.palette.text.disabled,
    display: 'inline-block',
    height: '2.5rem',
    marginLeft: '.25em',
    width: '.25em',
    [theme.breakpoints.up('md')]: { height: '4rem' },
    [theme.breakpoints.up('xxl')]: { height: '9rem' },
}));

const HelloSection: React.FC = () => {
    const [currentMessage, setCurrentMessage] = useState('');
    const [messageIndex, setMessageIndex] = useState(0);
    const [charIndex, setCharIndex] = useState(0);
    const [isDeleting, setIsDeleting] = useState(false);
    const [speed, setSpeed] = useState(TYPING_SPEED);
    const [showText, setShowText] = useState(true); // Controls the fade effect

    const timerRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        const handleTyping = (): void => {
            const fullText = messages[messageIndex];

            if (!isDeleting) {
                // Append next character for typing effect.
                const updatedText = fullText.substring(0, charIndex + 1);
                setCurrentMessage(updatedText);
                setCharIndex((prev) => prev + 1);

                if (updatedText === fullText) {
                    // Full message reached, pause before deleting.
                    setTimeout(() => setIsDeleting(true), PAUSE_DURATION);
                    setSpeed(DELETING_SPEED);
                }
            } else {
                // Remove a character for deleting effect.
                const updatedText = fullText.substring(0, charIndex - 1);
                setCurrentMessage(updatedText);
                setCharIndex((prev) => prev - 1);

                if (updatedText === '') {
                    // Once the message is fully deleted, fade out and move to the next message.
                    setShowText(false);
                    setTimeout(() => {
                        setIsDeleting(false);
                        setMessageIndex((prev) => (prev + 1) % messages.length);
                        setSpeed(TYPING_SPEED);
                        setCharIndex(0);
                        setShowText(true);
                    }, FADE_DURATION);
                }
            }
        };

        timerRef.current = setTimeout(handleTyping, speed);
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [charIndex, isDeleting, messageIndex, speed]);

    return (
        <Box
            alignItems="center"
            display="flex"
            flexDirection="column"
            justifyContent="center"
            sx={{
                height: { md: '300px', xs: '200px', xxl: '700px' },
                margin: '0 auto',
                marginTop: '120px',
                maxWidth: '100vw',
                px: 16,
                py: 4,
            }}
        >
            <Fade in={showText} timeout={FADE_DURATION}>
                <Box sx={{ alignItems: 'center', display: 'flex' }}>
                    <Typography color="secondary.main" fontSize={{ md: '4rem', xs: '2.5rem', xxl: '9rem' }}>
                        {currentMessage.charAt(0)}
                    </Typography>
                    <Typography fontSize={{ md: '4rem', xs: '2.5rem', xxl: '9rem' }}>
                        {currentMessage.slice(1)}
                    </Typography>
                    <Cursor />
                </Box>
            </Fade>
        </Box>
    );
};

export default HelloSection;
